import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaCheckCircle, FaPlus, FaTrash, FaSave, FaStar,
         FaUpload, FaFilePdf, FaFileImage, FaExclamationTriangle, FaTimes, FaCheckDouble,
         FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatShortDate(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const emptyRow = () => ({ id: Date.now() + Math.random(), project: '', hours: ['', '', '', '', '', '', ''], notes: '' });

export default function Timesheet() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const initialWeek = (() => {
    const w = searchParams.get('week');
    if (w) { const d = new Date(w + 'T00:00:00'); if (!isNaN(d)) return getMonday(d); }
    return getMonday(new Date());
  })();

  const [weekStart, setWeekStart]   = useState(initialWeek);
  const [rows, setRows]             = useState([emptyRow()]);
  const [globalNotes, setGlobalNotes] = useState('');
  const [submitted, setSubmitted]   = useState(false);
  const [tsStatus, setTsStatus]     = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [saving, setSaving]         = useState(false);
  const [projects, setProjects]     = useState([]);
  const [holidays, setHolidays]     = useState([]);
  const [loadError, setLoadError]   = useState('');
  const [saveError, setSaveError]   = useState('');

  // Client timesheet upload
  const fileInputRef                        = useRef(null);
  const [showClientUpload, setShowClientUpload] = useState(false); // checkbox toggle
  const [clientFile, setClientFile]         = useState(null);
  const [parsing, setParsing]               = useState(false);
  const [parseResult, setParseResult]       = useState(null);
  const [parseError, setParseError]         = useState('');
  const [dragOver, setDragOver]             = useState(false);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const dayDates = DAYS.map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  // Load projects from API
  useEffect(() => {
    api.getProjects()
      .then(setProjects)
      .catch(() => setLoadError('Could not load projects. Check server connection.'));
  }, []);

  // Load holidays for the current year
  useEffect(() => {
    const year = weekStart.getFullYear();
    api.getHolidays(year).then(setHolidays).catch(() => {});
  }, [weekStart]);

  // Load existing timesheet for the selected week
  useEffect(() => {
    setSubmitted(false);
    setTsStatus(null);
    setRejectionReason('');
    setSaveError('');
    api.getTimesheets()
      .then(timesheets => {
        const weekKey = weekStart.toISOString().split('T')[0];
        const existing = timesheets.find(t => t.week_start && t.week_start.startsWith(weekKey));
        if (existing) {
          api.getTimesheet(existing.id).then(ts => {
            setGlobalNotes(ts.global_notes || '');
            setTsStatus(ts.status);
            setRejectionReason(ts.rejection_reason || '');
            if (ts.rows && ts.rows.length > 0) {
              setRows(ts.rows.map(r => ({
                id: r.id,
                project: r.project_name || '',
                projectId: r.project_id,
                hours: [
                  r.mon?.toString() || '',
                  r.tue?.toString() || '',
                  r.wed?.toString() || '',
                  r.thu?.toString() || '',
                  r.fri?.toString() || '',
                  r.sat?.toString() || '',
                  r.sun?.toString() || '',
                ],
                notes: r.notes || '',
              })));
            } else {
              setRows([emptyRow()]);
            }
            if (ts.status === 'submitted' || ts.status === 'approved') {
              setSubmitted(true);
            }
          }).catch(() => {});
        } else {
          // Auto-fill holiday hours for new week
          const holidayRow = emptyRow();
          holidayRow.project = 'Holiday';
          let hasHoliday = false;
          dayDates.forEach((d, i) => {
            const h = holidays.find(h => h.date.startsWith(d.toISOString().split('T')[0]));
            if (h) { holidayRow.hours[i] = '8'; hasHoliday = true; }
          });
          setRows(hasHoliday ? [holidayRow] : [emptyRow()]);
          setGlobalNotes('');
        }
      })
      .catch(() => {});
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns holiday object if a given date is a holiday
  const getHoliday = (date) => {
    const key = date.toISOString().split('T')[0];
    return holidays.find(h => h.date.startsWith(key));
  };

  const prevWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d);
  };

  const handleHourChange = (rowId, dayIdx, value) => {
    if (value !== '' && (isNaN(value) || Number(value) < 0 || Number(value) > 24)) return;
    setRows(rows.map(r =>
      r.id === rowId ? { ...r, hours: r.hours.map((h, i) => i === dayIdx ? value : h) } : r
    ));
  };

  const handleProjectChange = (rowId, value) => {
    const proj = projects.find(p => p.name === value);
    setRows(rows.map(r => r.id === rowId ? { ...r, project: value, projectId: proj?.id || null } : r));
  };

  const handleNotesChange = (rowId, value) => {
    setRows(rows.map(r => r.id === rowId ? { ...r, notes: value } : r));
  };

  const addRow    = () => setRows([...rows, emptyRow()]);
  const removeRow = (rowId) => { if (rows.length > 1) setRows(rows.filter(r => r.id !== rowId)); };

  const rowTotal  = (hours) => hours.reduce((sum, h) => sum + (parseFloat(h) || 0), 0);
  const dayTotal  = (dayIdx) => rows.reduce((sum, r) => sum + (parseFloat(r.hours[dayIdx]) || 0), 0);
  const grandTotal = rows.reduce((sum, r) => sum + rowTotal(r.hours), 0);

  const save = async (action) => {
    setSaving(true);
    setSaveError('');
    try {
      // Build attachment payload if user uploaded a client timesheet
      let clientAttachment = null;
      if (parseResult && clientFile) {
        clientAttachment = {
          originalName:   parseResult.originalName  || clientFile.name,
          savedFilename:  parseResult.savedFilename  || null,
          storageType:    parseResult.storageType    || 'disk',
          fileBase64:     parseResult.fileBase64     || null,
          mimetype:       clientFile.type,
          fileSize:       clientFile.size,
        };
      }
      await api.saveTimesheet({
        weekStart: weekStart.toISOString(),
        rows,
        globalNotes,
        action,
        clientAttachment,
      });
      if (action === 'submit') setSubmitted(true);
      else alert('Draft saved successfully!');
    } catch (err) {
      setSaveError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit      = (e) => { e.preventDefault(); save('submit'); };
  const handleSaveDraft   = ()  => save('draft');

  // ── Client timesheet upload & parse ──────────────────────────
  const handleFileSelect = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setParseError('Only PDF, PNG, JPG or WEBP files are accepted.');
      return;
    }
    setClientFile(file);
    setParseResult(null);
    setParseError('');
    setParsing(true);
    try {
      const result = await api.parseClientHours(file);
      setParseResult(result);
    } catch (err) {
      setParseError(err.message || 'Could not parse the file.');
    } finally {
      setParsing(false);
    }
  };

  const clearUpload = () => {
    setClientFile(null);
    setParseResult(null);
    setParseError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClientUploadToggle = (checked) => {
    setShowClientUpload(checked);
    if (!checked) clearUpload(); // clear file + results when unchecked
  };

  const hourDiff = parseResult?.extractedTotal != null
    ? Math.abs(parseFloat((parseResult.extractedTotal - grandTotal).toFixed(2)))
    : null;
  const hoursMatch = hourDiff !== null && hourDiff < 0.1;

  if (submitted) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaCheckCircle className="text-emerald-500" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-[#0c1b4d] mb-2">Timesheet Submitted!</h2>
          <p className="text-gray-500 mb-2">
            Your timesheet for <strong>{formatShortDate(weekStart)} – {formatShortDate(weekEnd)}</strong> has been submitted for approval.
          </p>
          <p className="text-sm text-gray-400 mb-7">Total Hours: <strong className="text-[#0c1b4d]">{grandTotal.toFixed(1)}h</strong></p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { setSubmitted(false); setRows([emptyRow()]); nextWeek(); }}
              className="px-6 py-3 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors"
            >
              Submit Next Week
            </button>
            <Link to="/employee" className="px-6 py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-slate-50 transition-colors">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <p className="text-blue-300 text-xs mb-1">
                <Link to="/employee" className="hover:text-white">Dashboard</Link> / Timesheet
              </p>
              <h1 className="text-xl font-bold">Submit Timesheet</h1>
              <p className="text-blue-200 text-sm">{user?.name} · {user?.employeeId}</p>
            </div>
            <div className="flex items-center gap-2 bg-white bg-opacity-10 rounded-xl px-4 py-2.5">
              <button onClick={prevWeek} className="p-1 hover:text-cyan-400 transition-colors">
                <FaChevronLeft size={13} />
              </button>
              <span className="text-sm font-semibold px-2 whitespace-nowrap">
                {formatShortDate(weekStart)} – {formatShortDate(weekEnd)}
              </span>
              <button onClick={nextWeek} className="p-1 hover:text-cyan-400 transition-colors">
                <FaChevronRight size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">

        {loadError && (
          <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
            {loadError}
          </div>
        )}

        {saveError && (
          <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {saveError}
          </div>
        )}

        {tsStatus === 'rejected' && (
          <div className="mb-5 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <FaTimesCircle className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
            <div>
              <p className="text-sm font-semibold text-red-700">This timesheet was rejected — please revise and resubmit.</p>
              {rejectionReason && (
                <p className="text-sm text-red-600 mt-1"><span className="font-medium">Reviewer note:</span> {rejectionReason}</p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* ── Mobile: card-per-project layout ── */}
          <div className="md:hidden space-y-4 mb-5">
            {rows.map((row) => {
              const total = rowTotal(row.hours);
              return (
                <div key={row.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-gray-100">
                    <select
                      value={row.project}
                      onChange={e => handleProjectChange(row.id, e.target.value)}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 bg-white"
                    >
                      <option value="">-- Select Project --</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.name}>{p.client ? `${p.client} – ${p.name}` : p.name}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => removeRow(row.id)} disabled={rows.length === 1}
                      className="p-2 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-30">
                      <FaTrash size={13} />
                    </button>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {DAYS.map((day, i) => {
                      const holiday = getHoliday(dayDates[i]);
                      return (
                      <div key={day} className={`flex items-center px-4 py-2.5 gap-3 ${holiday ? 'bg-amber-50' : ''}`}>
                        <div className="w-24 flex-shrink-0">
                          <span className={`text-sm font-medium ${holiday ? 'text-amber-600' : 'text-gray-700'}`}>{day}</span>
                          <span className="text-xs text-gray-400 ml-1.5">{formatShortDate(dayDates[i])}</span>
                          {holiday && <div className="text-xs text-amber-500 font-medium"><FaStar size={8} className="inline mr-0.5" />{holiday.name}</div>}
                        </div>
                        <input
                          type="number" min="0" max="24" step="0.5"
                          value={row.hours[i]}
                          onChange={e => handleHourChange(row.id, i, e.target.value)}
                          className="w-20 text-center text-sm border border-gray-200 rounded-lg py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-400">hrs</span>
                      </div>
                      );
                    })}
                  </div>
                  <div className="px-4 py-3 bg-slate-50 border-t border-gray-100 flex items-center gap-3">
                    <input type="text" value={row.notes} onChange={e => handleNotesChange(row.id, e.target.value)}
                      placeholder="Notes (optional)..."
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-600 bg-white"
                    />
                    <div className="flex-shrink-0 text-right">
                      <div className="text-xs text-gray-400">Total</div>
                      <div className={`text-base font-bold ${total > 0 ? 'text-[#0c1b4d]' : 'text-gray-300'}`}>
                        {total > 0 ? `${total.toFixed(1)}h` : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-between">
              <button type="button" onClick={addRow}
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                <FaPlus size={11} /> Add Project
              </button>
              <div className="bg-[#0c1b4d] text-white rounded-xl px-4 py-2 text-sm font-semibold">
                Total: <span className="text-cyan-400">{grandTotal.toFixed(1)}h</span>
              </div>
            </div>
          </div>

          {/* ── Desktop: full table ── */}
          <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-[#0c1b4d] min-w-[200px]">Project / Client</th>
                    {DAYS.map((day, i) => {
                      const holiday = getHoliday(dayDates[i]);
                      return (
                        <th key={day} className={`px-2 py-3 font-semibold text-center min-w-[70px] ${holiday ? 'bg-amber-50' : ''}`}>
                          <div className={holiday ? 'text-amber-600' : 'text-[#0c1b4d]'}>{day}</div>
                          <div className="text-xs font-normal text-gray-400">{formatShortDate(dayDates[i])}</div>
                          {holiday && <div className="text-xs text-amber-500 font-medium truncate max-w-[70px]"><FaStar size={8} className="inline mr-0.5" />{holiday.name}</div>}
                        </th>
                      );
                    })}
                    <th className="px-3 py-3 font-semibold text-[#0c1b4d] text-center min-w-[60px]">Total</th>
                    <th className="px-3 py-3 font-semibold text-[#0c1b4d] text-left min-w-[140px]">Notes</th>
                    <th className="px-2 py-3 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((row) => {
                    const total = rowTotal(row.hours);
                    return (
                      <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-2.5">
                          <select
                            value={row.project}
                            onChange={e => handleProjectChange(row.id, e.target.value)}
                            className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
                          >
                            <option value="">-- Select Project --</option>
                            {projects.map(p => (
                              <option key={p.id} value={p.name}>{p.client ? `${p.client} – ${p.name}` : p.name}</option>
                            ))}
                          </select>
                        </td>
                        {row.hours.map((h, i) => (
                          <td key={i} className="px-1.5 py-2.5">
                            <input
                              type="number" min="0" max="24" step="0.5"
                              value={h}
                              onChange={e => handleHourChange(row.id, i, e.target.value)}
                              className="w-full text-center text-sm border border-gray-200 rounded-lg py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0"
                            />
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-center">
                          <span className={`font-semibold text-sm ${total > 0 ? 'text-[#0c1b4d]' : 'text-gray-300'}`}>
                            {total > 0 ? total.toFixed(1) : '—'}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          <input type="text" value={row.notes}
                            onChange={e => handleNotesChange(row.id, e.target.value)}
                            placeholder="Optional notes..."
                            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-600"
                          />
                        </td>
                        <td className="px-2 py-2.5">
                          <button type="button" onClick={() => removeRow(row.id)}
                            className="text-gray-300 hover:text-red-400 transition-colors p-1" disabled={rows.length === 1}>
                            <FaTrash size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t border-gray-200 font-semibold">
                    <td className="px-4 py-3 text-sm text-[#0c1b4d]">Daily Totals</td>
                    {DAYS.map((_, i) => {
                      const dt = dayTotal(i);
                      return (
                        <td key={i} className="px-1.5 py-3 text-center text-sm">
                          <span className={dt > 0 ? 'text-[#0c1b4d]' : 'text-gray-300'}>{dt > 0 ? dt.toFixed(1) : '—'}</span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-center text-[#0c1b4d] text-base">{grandTotal.toFixed(1)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100">
              <button type="button" onClick={addRow}
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                <FaPlus size={11} /> Add Project Row
              </button>
            </div>
          </div>

          {/* ── Client Timesheet Upload ── */}
          <div className="mb-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Checkbox toggle row */}
              <div className="flex items-center justify-between px-5 py-3.5">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showClientUpload}
                    onChange={e => handleClientUploadToggle(e.target.checked)}
                    className="w-4 h-4 accent-blue-700"
                  />
                  <div>
                    <span className="font-bold text-[#0c1b4d] text-sm flex items-center gap-2">
                      <FaUpload size={13} /> Attach Client Timesheet
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">Check to upload the client's timesheet and compare hours</p>
                  </div>
                </label>
                {showClientUpload && clientFile && (
                  <button type="button" onClick={clearUpload}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1">
                    <FaTimes size={14} />
                  </button>
                )}
              </div>

              {/* Upload panel — only visible when checkbox is ticked */}
              {showClientUpload && (
              <div className="p-5 border-t border-gray-100">
                {/* Drop zone — only shown when no file selected */}
                {!clientFile && (
                  <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); handleFileSelect(e.dataTransfer.files[0]); }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    <FaUpload size={22} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-600">Drop file here or <span className="text-blue-600">browse</span></p>
                    <p className="text-xs text-gray-400 mt-1">PDF · PNG · JPG · WEBP — max 10 MB</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={e => handleFileSelect(e.target.files[0])}
                    />
                  </div>
                )}

                {/* File selected — show name + status */}
                {clientFile && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                      {clientFile.type === 'application/pdf'
                        ? <FaFilePdf size={20} className="text-red-400 flex-shrink-0" />
                        : <FaFileImage size={20} className="text-blue-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{clientFile.name}</p>
                        <p className="text-xs text-gray-400">{(clientFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                      {parsing && (
                        <span className="text-xs text-blue-500 font-medium flex items-center gap-1.5">
                          <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                          </svg>
                          Extracting hours…
                        </span>
                      )}
                    </div>

                    {/* Parse error */}
                    {parseError && (
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                        <FaExclamationTriangle size={13} className="mt-0.5 flex-shrink-0" /> {parseError}
                      </div>
                    )}

                    {/* Scanned PDF warning */}
                    {parseResult?.warning && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                        <FaExclamationTriangle size={13} className="mt-0.5 flex-shrink-0" /> {parseResult.warning}
                      </div>
                    )}

                    {/* Result — hours extracted */}
                    {parseResult && parseResult.extractedTotal != null && (
                      <div className={`rounded-xl border px-5 py-4 ${
                        hoursMatch
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          {/* Client hours */}
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Client Timesheet</p>
                            <p className="text-3xl font-bold text-[#0c1b4d]">
                              {parseResult.extractedTotal.toFixed(1)}
                              <span className="text-base font-normal text-gray-400 ml-1">h</span>
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Extracted via {parseResult.engine === 'tesseract' ? 'OCR' : 'PDF text'} · method: {parseResult.method}
                            </p>
                          </div>

                          {/* vs divider */}
                          <div className="hidden sm:flex flex-col items-center gap-1 text-gray-400 text-xs font-semibold">
                            <div className="w-px h-8 bg-gray-200" />
                            VS
                            <div className="w-px h-8 bg-gray-200" />
                          </div>

                          {/* Your entered hours */}
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Your Entered Hours</p>
                            <p className="text-3xl font-bold text-[#0c1b4d]">
                              {grandTotal.toFixed(1)}
                              <span className="text-base font-normal text-gray-400 ml-1">h</span>
                            </p>
                          </div>

                          {/* Match / mismatch badge */}
                          <div className="flex-shrink-0">
                            {hoursMatch ? (
                              <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-xl px-4 py-2.5">
                                <FaCheckDouble size={14} />
                                <div>
                                  <p className="text-sm font-bold">Hours Match</p>
                                  <p className="text-xs opacity-75">Good to submit</p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 bg-amber-100 text-amber-800 rounded-xl px-4 py-2.5">
                                <FaExclamationTriangle size={14} />
                                <div>
                                  <p className="text-sm font-bold">
                                    {parseResult.extractedTotal > grandTotal ? '+' : '-'}{hourDiff.toFixed(1)}h difference
                                  </p>
                                  <p className="text-xs opacity-75">
                                    {parseResult.extractedTotal > grandTotal
                                      ? 'Client shows more hours'
                                      : 'Client shows fewer hours'}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Raw text preview toggle */}
                        {parseResult.rawTextPreview && (
                          <details className="mt-3">
                            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                              Show extracted text preview
                            </summary>
                            <pre className="mt-2 text-xs text-gray-500 bg-white border border-gray-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {parseResult.rawTextPreview}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Notes & Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Additional Notes</label>
              <textarea
                rows={3}
                value={globalNotes}
                onChange={e => setGlobalNotes(e.target.value)}
                placeholder="Any additional context about this week's work (optional)..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
            <div className="bg-[#0c1b4d] rounded-2xl p-5 text-white">
              <h3 className="font-bold text-sm mb-4">Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-300">Period</span>
                  <span className="font-medium text-xs">{formatShortDate(weekStart)} – {formatShortDate(weekEnd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-300">Projects</span>
                  <span className="font-medium">{rows.filter(r => r.project && rowTotal(r.hours) > 0).length}</span>
                </div>
                <div className="border-t border-white border-opacity-10 pt-2 mt-2 flex justify-between">
                  <span className="text-blue-300">Total Hours</span>
                  <span className="text-cyan-400 font-bold text-lg">{grandTotal.toFixed(1)}h</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving || grandTotal === 0}
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <><FaCheckCircle size={13} /> Submit for Approval</>
              )}
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <FaSave size={13} /> Save Draft
            </button>
            <Link to="/employee" className="inline-flex items-center gap-2 px-5 py-3 text-gray-500 font-medium hover:text-gray-700 transition-colors">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
